import { motion, AnimatePresence } from "motion/react";
import { useState } from "react";

interface PasswordChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function PasswordChangeModal({
  isOpen,
  onClose,
  onSuccess,
}: PasswordChangeModalProps) {
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const handleSave = () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert("Passwords do not match!");
      return;
    }
    onSuccess();
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-5"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="bg-white rounded-3xl p-6 w-full max-w-md"
            style={{
              boxShadow:
                "0 20px 60px rgba(0, 0, 0, 0.3), 0 0 100px rgba(27, 42, 91, 0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2
                className="text-[#0A0F1C]"
                style={{
                  fontSize: "24px",
                  letterSpacing: "-0.02em",
                  fontWeight: "700",
                }}
              >
                Change Password
              </h2>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={onClose}
                className="w-10 h-10 rounded-full bg-[#F9FAFB] flex items-center justify-center"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M5 5L15 15M5 15L15 5"
                    stroke="#0A0F1C"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </motion.button>
            </div>

            {/* Form */}
            <div className="space-y-5">
              <div>
                <label
                  className="text-[#717182] block mb-2"
                  style={{
                    fontSize: "11px",
                    letterSpacing: "0.05em",
                    fontWeight: "600",
                  }}
                >
                  CURRENT PASSWORD
                </label>
                <div className="relative">
                  <input
                    type={showPassword.current ? "text" : "password"}
                    className="w-full px-4 py-3 pr-12 rounded-xl bg-[#F9FAFB] text-[#0A0F1C] border-2 border-transparent focus:border-[#1B2A5B] outline-none transition-colors"
                    style={{ fontSize: "15px", fontWeight: "500" }}
                    value={passwordForm.currentPassword}
                    onChange={(e) =>
                      setPasswordForm({
                        ...passwordForm,
                        currentPassword: e.target.value,
                      })
                    }
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword({
                        ...showPassword,
                        current: !showPassword.current,
                      })
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#717182]"
                  >
                    {showPassword.current ? "👁️" : "🔒"}
                  </button>
                </div>
              </div>

              <div>
                <label
                  className="text-[#717182] block mb-2"
                  style={{
                    fontSize: "11px",
                    letterSpacing: "0.05em",
                    fontWeight: "600",
                  }}
                >
                  NEW PASSWORD
                </label>
                <div className="relative">
                  <input
                    type={showPassword.new ? "text" : "password"}
                    className="w-full px-4 py-3 pr-12 rounded-xl bg-[#F9FAFB] text-[#0A0F1C] border-2 border-transparent focus:border-[#1B2A5B] outline-none transition-colors"
                    style={{ fontSize: "15px", fontWeight: "500" }}
                    value={passwordForm.newPassword}
                    onChange={(e) =>
                      setPasswordForm({
                        ...passwordForm,
                        newPassword: e.target.value,
                      })
                    }
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword({
                        ...showPassword,
                        new: !showPassword.new,
                      })
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#717182]"
                  >
                    {showPassword.new ? "👁️" : "🔒"}
                  </button>
                </div>
              </div>

              <div>
                <label
                  className="text-[#717182] block mb-2"
                  style={{
                    fontSize: "11px",
                    letterSpacing: "0.05em",
                    fontWeight: "600",
                  }}
                >
                  CONFIRM NEW PASSWORD
                </label>
                <div className="relative">
                  <input
                    type={showPassword.confirm ? "text" : "password"}
                    className="w-full px-4 py-3 pr-12 rounded-xl bg-[#F9FAFB] text-[#0A0F1C] border-2 border-transparent focus:border-[#1B2A5B] outline-none transition-colors"
                    style={{ fontSize: "15px", fontWeight: "500" }}
                    value={passwordForm.confirmPassword}
                    onChange={(e) =>
                      setPasswordForm({
                        ...passwordForm,
                        confirmPassword: e.target.value,
                      })
                    }
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword({
                        ...showPassword,
                        confirm: !showPassword.confirm,
                      })
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#717182]"
                  >
                    {showPassword.confirm ? "👁️" : "🔒"}
                  </button>
                </div>
              </div>

              {/* Password Strength Indicator */}
              <div className="p-4 rounded-xl bg-[#F9FAFB]">
                <div
                  className="text-[#717182] mb-2"
                  style={{ fontSize: "11px", fontWeight: "600" }}
                >
                  Password Requirements:
                </div>
                <div className="space-y-1">
                  {[
                    "At least 8 characters",
                    "One uppercase letter",
                    "One lowercase letter",
                    "One number",
                  ].map((req, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 text-[#717182]"
                      style={{ fontSize: "12px" }}
                    >
                      <div
                        className="w-1 h-1 rounded-full"
                        style={{ backgroundColor: "#717182" }}
                      />
                      {req}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 mt-8">
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                className="flex-1 py-3 px-4 rounded-xl border-2 border-[#E5E7EB] text-[#717182]"
                style={{ fontSize: "15px", fontWeight: "700" }}
              >
                Cancel
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={handleSave}
                className="flex-1 py-3 px-4 rounded-xl text-white"
                style={{
                  background:
                    "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
                  fontSize: "15px",
                  fontWeight: "700",
                  boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
                }}
              >
                Update Password
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}